import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faBars, faDownload } from "@fortawesome/free-solid-svg-icons";

import { Grid, IconButton, Button, TextField, Typography } from "@mui/material";

import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import AddNewDocument from "../../../../../containers/AddNewDocument/AddNewDocument";

import { LegalEntityEventDTO } from "../../../../../models/legal-entity-events";
import { DocumentDTO } from "../../../../../models/documents";

import { EventTypesApiService } from "../../../../../services/event-types/event-types-api.service";
import { saveLegalEntityEvent } from "../../../../../services/legal-entity-events/legal-entity-events-api.service";
import { loadLegalEntityEvents } from "../../../../../services/legal-entity-events/legal-entity-events-api.service";
import { handleDownloadDocsFiles } from "../../../../../services/documents/document-files-api.service";

import { convertDateToUTC } from "../../../../../utils/time-utils";
import { intlFormatDateShort } from "../../../../../utils/time-utils";

import { Props } from "./interfaces";
import { Container, EventHeader, Actions, EventContent, FilesList, FilesInfo } from "./styles";
import AttachedDocuments from "src/containers/AttachedDocuments/AttachedDocuments";
import { DesktopDatePicker } from "@mui/x-date-pickers";

const LegalEntityEvent = ({ legalEntityEvent, propsFilter, corporateChanges, legalEntities }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.document_reports.compliance_documents.assemblies_audits_corporate_changes.add_new_event"; // tRP - translation root path
  const theme = useTheme();
  const filter = useMemo(() => propsFilter, [propsFilter]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [event, setEvent] = useState<LegalEntityEventDTO>(legalEntityEvent);

  const handleChangeEvent = (field: keyof LegalEntityEventDTO, value: LegalEntityEventDTO[keyof LegalEntityEventDTO]) =>
    setEvent((event) => ({ ...event, [field]: value }));

  const handleSaveEvent = async (event: LegalEntityEventDTO) => {
    setLoading(true);

    try {
      const { error } = await saveLegalEntityEvent({ ...event, date: convertDateToUTC(event.date) });

      if (!error) setEditMode(false);
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  };

  const loadEvent = useCallback(async () => {
    try {
      const response = await loadLegalEntityEvents({
        ids: [event?.id],
        ...filter,
      });
      if (!response.error) setEvent(response.items[0]);
      else setError(response.error as string);
    } catch (error) {
      setError(error as string);
    }
  }, [event?.id, filter]);

  const handleDeleteDocumentFromEvent = useCallback(async (docId: string) => {
    setLoading(true);

    try {
      const saveResponse = await saveLegalEntityEvent({
        ...event,
        documents: [...event.documents.filter((doc) => doc.id !== docId)],
      });

      if (!saveResponse.error) {
        loadEvent();
      }
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveDocsToEvent = async (doc: DocumentDTO | null) => {
    if (!doc) return;

    setLoading(true);

    try {
      const saveResponse = await saveLegalEntityEvent({
        ...event,
        documents: [...event.documents, doc],
      });

      if (!saveResponse.error) {
        loadEvent();
      }
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingOverlay
      loading={loading}
      error={error}
    >
      <Container>
        <EventHeader>
          <Grid>
            {editMode ? (
              <AutocompleteDropdown
                labelKey={`${t(`${tRP}.event_fields.event_type`)}`}
                serviceApi={new EventTypesApiService()}
                propsItem={event}
                field="eventType"
                handleChange={handleChangeEvent}
              />
            ) : (
              <Typography>{event?.eventType?.name}</Typography>
            )}
            {editMode ? (
              <DesktopDatePicker
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t(`${tRP}.event_fields.date`)}`}
                  />
                )}
                value={event.date}
                onChange={(value) => handleChangeEvent("date", value)}
              />
            ) : (
              <Typography>{intlFormatDateShort(event?.date)}</Typography>
            )}
            {editMode ? (
              <TextField
                value={event?.name}
                onChange={({ target: { value } }) => handleChangeEvent("name", value)}
              />
            ) : (
              <Typography>{event?.name}</Typography>
            )}
          </Grid>
          <Actions>
            {editMode ? (
              <>
                <Button
                  variant="contained"
                  onClick={() => handleSaveEvent(event)}
                >
                  {`${t("buttons.save")}`}
                </Button>
                <Button
                  variant="contained"
                  // color="secondary"
                  style={{
                    color: theme.palette.primary.contrastText,
                    backgroundColor: theme.palette.actions.cancel,
                  }}
                  onClick={() => setEditMode(false)}
                >
                  {`${t("buttons.cancel")}`}
                </Button>
              </>
            ) : (
              <IconButton onClick={() => setEditMode(true)}>
                <FontAwesomeIcon icon={faPencil} />
              </IconButton>
            )}
          </Actions>
        </EventHeader>
        <EventContent>
          <AddNewDocument
            buttonType="plusButton"
            handleNotifyParent={handleSaveDocsToEvent}
            documentFieldsProps={{
              legalEntity: legalEntities.filter(({ id }) => id === filter.legalEntityIds[0])[0] ?? null,
              category: corporateChanges,
            }}
            closeAfterSave
          />
          {!!event?.documents.length && (
            <>
              {/* <FilesList>
                {event?.documents
                  .map((d) => d.files[0])
                  .map(
                    (file, idx) =>
                      file && (
                        <Grid
                          key={idx}
                          title={file?.name}
                        >
                          {getFontAwesomeIconFromMIME(file?.type)}
                          <Typography>{getFileExtension(file?.name)}</Typography>
                        </Grid>
                      )
                  )}
              </FilesList> */}
              <Grid style={{ display: "flex", justifyContent: "start", width: "100%" }}>
                {event?.documents.map((doc) => (
                  <AttachedDocuments
                    documentIDs={[doc.id]}
                    count={doc.filesCount}
                    handleNotifyParent={loadEvent}
                    handleNotifyParentUponDelete={handleDeleteDocumentFromEvent}
                    buttonSize="2x"
                  />
                ))}
              </Grid>

              <Actions>
                <Button onClick={() => handleDownloadDocsFiles(event?.documents.map((d) => d.id))}>
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
                <FilesInfo>
                  <Button>
                    <FontAwesomeIcon icon={faBars} />
                  </Button>
                  <Grid>
                    {event.documents.map((d) => (
                      <Typography key={d.id}>{`${d?.name} - ${intlFormatDateShort(d?.createdAt)}`}</Typography>
                    ))}
                  </Grid>
                </FilesInfo>
              </Actions>
            </>
          )}
        </EventContent>
      </Container>
    </LoadingOverlay>
  );
};

export default LegalEntityEvent;
